// One person on the family list: avatar, a name that links to their own page, the
// relationship label and where they stand ("Not yet invited", "Invited", "Joined"; nothing for
// someone who is not a collaborator on the plan).
//
// No role — "users do not think in terms of principal and interested party, and the
// interface does not present them" — and there is no role in this component to present,
// because `decodePerson` drops the field before it becomes a value anywhere in the browser.
//
// The reader's own row says "You" on the detail line, where the others' rows say how they
// are related, so the reader can find themselves on the list. Every hand action for a
// person (rename, email, delete, whether they collaborate, topics) is on their own page.

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { Person } from '../api/contract.js';
import { relationLabel } from '../format.js';

export type PersonRowProps = {
  person: Person;
  /** Where the name links to: the person's own page. Absent: the name is plain text. */
  to?: string;
  /** A body area under the name: a description line later. */
  children?: ReactNode;
  /** Extra class while the row is lit up after a change. */
  highlightClass?: string;
  /** "Changed by Maya · 1 Oct", when the list says who last changed this person. */
  changedNote?: string | null;
};

/** Two letters for the avatar: the first letters of two words, else the first two letters. */
export function initialsOf(name: string): string {
  const words = name
    .trim()
    .split(/\s+/u)
    .filter((word) => word !== '');
  const [first, second] = words;
  if (first === undefined) {
    return '?';
  }
  if (second !== undefined) {
    return (first.charAt(0) + second.charAt(0)).toUpperCase();
  }
  const letters = Array.from(first).slice(0, 2).join('');
  return letters.charAt(0).toUpperCase() + letters.slice(1).toLowerCase();
}

/** One of six muted hues, chosen from the name so a person keeps their colour. */
export function hueOf(name: string): number {
  let hash = 0;
  for (const char of name.trim().toLowerCase()) {
    hash = (hash * 31 + (char.codePointAt(0) ?? 0)) % 1009;
  }
  return hash % 6;
}

export function PersonRow({
  person,
  to,
  children,
  highlightClass,
  changedNote = null,
}: PersonRowProps): JSX.Element {
  const unnamed = person.name.trim() === '';
  const shownName = person.self && unnamed ? 'You' : person.name;
  const detail = [
    person.self && !unnamed ? 'You' : null,
    person.relation === null ? null : relationLabel(person.relation),
    !person.takesPart
      ? null
      : person.joined
        ? 'Joined'
        : person.invited
          ? 'Invited'
          : 'Not yet invited',
  ].filter((part): part is string => part !== null && part !== '');
  return (
    <li
      className={[person.self ? 'person person--self' : 'person', highlightClass]
        .filter((c) => c !== undefined && c !== '')
        .join(' ')}
    >
      <span
        className={`person__avatar person__avatar--${hueOf(person.name)}`}
        aria-hidden="true"
      >
        {initialsOf(person.name)}
      </span>
      <span className="person__identity">
        {to === undefined ? (
          <span className="person__name">{shownName}</span>
        ) : (
          <Link className="person__name person__name--link" to={to}>
            {shownName}
          </Link>
        )}
        {detail.length === 0 ? null : (
          <span className="person__detail">{detail.join(' · ')}</span>
        )}
        {changedNote === null ? null : (
          <span className="person__detail person__changed">{changedNote}</span>
        )}
        {children}
      </span>
    </li>
  );
}
